import React, { useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './shell.css';

/* Session storage can throw in locked-down browsers — never let the rail die for it. */
function ss(key) {
  try {
    return sessionStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
}

/* The one permission rule DashboardNav.js enforces today: the EShop group and
   the Admin Users entry are shown only when sessionStorage 'isAdmin' == 1.
   Loose comparison on purpose — that is exactly what DashboardNav.js does. */
export function isAdminUser() {
  return ss('isAdmin') == 1; // eslint-disable-line eqeqeq
}

/* ------------------------------------------------------------------
   ICONS — inline SVG, stroke: currentColor (ui.css paints .nav-item svg)
   ------------------------------------------------------------------ */
const IconDashboard = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="3" width="7" height="8" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="11" width="7" height="10" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
  </svg>
);

const IconAnalytics = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 4v16h16" />
    <path d="M8 20v-5M12.5 20v-9M17 20v-4" />
    <path d="M7 11l4.5-4.5 3 3L20 4" />
  </svg>
);

const IconContent = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M7 9h6M7 13h10M7 17h10" />
  </svg>
);

const IconCoach = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2.5" y="5" width="14" height="14" rx="2" />
    <path d="M16.5 10.5l5-3v9l-5-3z" />
  </svg>
);

const IconSponsor = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 9v6h3l6 4V5L7 9H4z" />
    <path d="M17.5 8.5a5 5 0 0 1 0 7" />
  </svg>
);

const IconCalendar = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </svg>
);

const IconVideos = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M10 9l5 3-5 3z" />
  </svg>
);

const IconFile = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </svg>
);

const IconUsers = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3 20c0-3.1 2.7-5.2 6-5.2s6 2.1 6 5.2" />
    <path d="M16.5 5.6a3.2 3.2 0 0 1 0 5.6M18 14.2c2 .8 3.3 2.4 3.3 4.6" />
  </svg>
);

const IconKey = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="7.5" cy="14.5" r="3.5" />
    <path d="M10 12l8-8M15.5 6.5l2 2M18.5 3.5l2 2" />
  </svg>
);

const IconReport = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 21V4h9l.6 2H20v9h-6l-.6-2H5" />
    <path d="M5 21v-6" />
  </svg>
);

const IconProduct = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M3 7.5l9-4.5 9 4.5v9L12 21l-9-4.5z" />
    <path d="M3 7.5l9 4.5 9-4.5M12 12v9" />
  </svg>
);

const IconCustomer = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" />
  </svg>
);

const IconShop = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 9h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    <path d="M4 9l1.6-5h12.8L20 9M9 21v-6h6v6" />
  </svg>
);

const IconSports = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3l3.2 5.4-1.6 5.6h-3.2L8.8 8.4z" />
    <path d="M12 21l-2.6-3.4M12 21l2.6-3.4M3.5 9.5l4.4-1M20.5 9.5l-4.4-1" />
  </svg>
);

const IconMap = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 4L3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5z" />
    <path d="M9 4v13M15 6.5v13" />
  </svg>
);

const IconGlobe = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.4 2.5 3.6 5.5 3.6 9S14.4 18.5 12 21c-2.4-2.5-3.6-5.5-3.6-9S9.6 5.5 12 3z" />
  </svg>
);

const IconBell = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9z" />
    <path d="M10 18.5a2 2 0 0 0 4 0" />
  </svg>
);

const IconMenu = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

const IconClose = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/**
 * NAV GROUPS
 * Labels are the ones DashboardNav.js already used — the Greek strings are
 * carried over verbatim. `adminOnly` reproduces its isAdmin == 1 checks.
 */
export const NAV_GROUPS = [
  {
    label: null,
    items: [{ to: '/dashboard', text: 'Dashboard', icon: IconDashboard }]
  },
  /* Reporting sits in its own group, directly under Dashboard: it is the only
     read-only screen in the rail and it does not belong inside Content or
     People. It is adminOnly for the same reason the EShop group is — the page
     carries revenue, abandoned-basket value and subscription figures. */
  {
    label: 'Reporting',
    adminOnly: true,
    items: [{ to: '/analytics', text: 'Analytics', icon: IconAnalytics }]
  },
  {
    label: 'Content',
    items: [
      { to: '/challenges', text: 'Content', icon: IconContent },
      { to: '/coachesvideos', text: "Coaches' Videos", icon: IconCoach },
      { to: '/sponsors', text: 'Sponsors', icon: IconSponsor },
      { to: '/events', text: 'Events', icon: IconCalendar },
      { to: '/videos', text: 'Videos', icon: IconVideos },
      { to: '/sharedfiles', text: 'Shared Files', icon: IconFile }
    ]
  },
  {
    label: 'People',
    items: [
      { to: '/users', text: 'Users', icon: IconUsers },
      { to: '/adminusers', text: 'Admin Users', icon: IconKey, adminOnly: true },
      { to: '/contentreports', text: 'Content Reports', icon: IconReport }
    ]
  },
  {
    label: 'EShop',
    adminOnly: true,
    items: [
      { to: '/eshopproducts', text: 'Products', icon: IconProduct },
      { to: '/eshopcustomers', text: 'Customers', icon: IconCustomer },
      { to: '/eshopsettings', text: 'Settings', icon: IconShop }
    ]
  },
  {
    label: 'Settings',
    items: [
      { to: '/sports', text: 'Sports', icon: IconSports },
      { to: '/countries', text: 'Countries', icon: IconMap },
      { to: '/lang', text: 'Content Languages', icon: IconGlobe },
      { to: '/pushnotifications', text: 'Push Notifications', icon: IconBell }
    ]
  }
];

/** The groups the current session may actually see. */
export function visibleGroups() {
  const admin = isAdminUser();
  return NAV_GROUPS.filter((group) => !group.adminOnly || admin).map((group) => ({
    label: group.label,
    items: group.items.filter((item) => !item.adminOnly || admin)
  })).filter((group) => group.items.length > 0);
}

/**
 * The rail. Above 860px it is a sticky column; below that ui.css lays it out as a
 * horizontal strip and this component turns it into a disclosure drawer, because
 * the admin has sixteen destinations and a side-scrolling strip of sixteen chips
 * is unusable on a phone.
 */
export default function Sidebar({ open, onToggle, onNavigate }) {
  const location = useLocation();
  const railRef = useRef(null);
  const isOpen = open === true;

  /* Close the drawer whenever the route changes. */
  useEffect(() => {
    if (isOpen && typeof onNavigate === 'function') onNavigate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* Escape and outside clicks close it too — only while it is open. */
  useEffect(() => {
    if (!isOpen) return undefined;

    const close = () => {
      if (typeof onNavigate === 'function') onNavigate();
    };
    const onDown = (ev) => {
      if (railRef.current && !railRef.current.contains(ev.target)) close();
    };
    const onKey = (ev) => {
      if (ev.key === 'Escape') close();
    };

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onNavigate]);

  const groups = visibleGroups();

  return (
    <aside className={isOpen ? 'rail rail-open' : 'rail'} ref={railRef}>
      <div className="rail-head">
        <Link className="brand-link" to="/dashboard" aria-label="123 High Admin — dashboard">
          <img className="brand-img" src={process.env.PUBLIC_URL + '/logo.png'} alt="123 High" />
          <span className="brand-text">
            <span className="brand-name">123 High</span>
            <span className="brand-sub">Admin</span>
          </span>
        </Link>

        <button
          type="button"
          className="rail-toggle"
          aria-expanded={isOpen}
          aria-controls="admin-nav"
          aria-label={isOpen ? 'Close menu' : 'Menu'}
          onClick={onToggle}
        >
          {isOpen ? IconClose : IconMenu}
        </button>
      </div>

      <nav className="nav" id="admin-nav" aria-label="Main">
        {groups.map((group, gi) => (
          <React.Fragment key={group.label || 'top'}>
            {group.label && (
              <div className={gi === 0 ? 'mono nav-label' : 'mono nav-label nav-gap'}>
                {group.label}
              </div>
            )}
            {group.items.map((item) => (
              <NavLink key={item.to} className="nav-item" to={item.to} aria-label={item.text}>
                {item.icon}
                <span className="nav-text">{item.text}</span>
              </NavLink>
            ))}
          </React.Fragment>
        ))}
      </nav>
    </aside>
  );
}
